<template>
  <div
    ref="chart"
    class="echarts-line"
    role="img"
    :aria-label="ariaLabel"
  />
</template>

<script>
import echarts from '@/plugin/echarts'
import { localeState } from '@/i18n'

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export default {
  name: 'EChartLine',
  props: {
    points: { type: Array, required: true },
    labels: { type: Array, default: () => [] },
    color: { type: String, default: '#1f6bff' },
    strokeWidth: { type: Number, default: 2.5 },
    areaOpacity: { type: Number, default: 0.2 },
    smooth: { type: Number, default: 0.36 },
    showAxis: { type: Boolean, default: false },
    axisLabelCount: { type: Number, default: 6 },
    axisFontSize: { type: Number, default: 10 },
    gridTop: { type: Number, default: null },
    gridBottom: { type: Number, default: null },
    splitNumber: { type: Number, default: null },
    tooltipMode: { type: String, default: 'default' },
    tooltipRates: { type: Array, default: () => [] },
    ariaLabel: { type: String, default: '收益趋势折线图' }
  },
  data() {
    return {
      chart: null,
      resizeObserver: null
    }
  },
  computed: {
    currentLocale() {
      return localeState.current
    }
  },
  watch: {
    currentLocale() {
      this.renderChart()
    },
    points: {
      deep: true,
      handler() {
        this.renderChart()
      }
    },
    labels: {
      deep: true,
      handler() {
        this.renderChart()
      }
    },
    color() {
      this.renderChart()
    },
    strokeWidth() {
      this.renderChart()
    },
    areaOpacity() {
      this.renderChart()
    },
    smooth() {
      this.renderChart()
    },
    showAxis() {
      this.renderChart()
    },
    axisLabelCount() {
      this.renderChart()
    },
    axisFontSize() {
      this.renderChart()
    },
    gridTop() {
      this.renderChart()
    },
    gridBottom() {
      this.renderChart()
    },
    splitNumber() {
      this.renderChart()
    },
    tooltipMode() {
      this.renderChart()
    },
    tooltipRates: {
      deep: true,
      handler() {
        this.renderChart()
      }
    }
  },
  mounted() {
    this.chart = echarts.init(this.$refs.chart, null, { renderer: 'canvas' })
    this.renderChart()

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.resize())
      this.resizeObserver.observe(this.$refs.chart)
    } else {
      window.addEventListener('resize', this.resize)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.resize)
    }

    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    resize() {
      if (this.chart) this.chart.resize()
    },
    renderChart() {
      if (!this.chart) return

      const labels = this.labels.length === this.points.length
        ? this.labels
        : this.points.map((_, index) => String(index + 1))
      const labelCount = Math.max(2, Math.min(labels.length, this.axisLabelCount))
      const visibleLabelIndexes = new Set(
        Array.from({ length: labelCount }, (_, index) =>
          Math.round(index * (labels.length - 1) / (labelCount - 1))
        )
      )
      const overviewTooltip = this.tooltipMode === 'overview'
      const tooltipRates = this.tooltipRates
      const numberLocale = this.currentLocale === 'en'
        ? 'en-US'
        : (this.currentLocale === 'ko' ? 'ko-KR' : this.currentLocale)
      const translate = text => this.$t(text)

      this.chart.setOption(
        {
          animationDuration: 650,
          aria: {
            enabled: true,
            description: translate(this.ariaLabel)
          },
          grid: {
            top: this.gridTop === null ? 8 : this.gridTop,
            right: 2,
            bottom: this.gridBottom === null ? (this.showAxis ? 27 : 2) : this.gridBottom,
            left: 2,
            containLabel: false
          },
          tooltip: {
            trigger: 'axis',
            confine: true,
            backgroundColor: overviewTooltip ? '#171A27' : 'rgba(8, 23, 55, 0.92)',
            borderWidth: 0,
            padding: overviewTooltip ? [10, 14] : [8, 10],
            extraCssText: overviewTooltip
              ? 'border-radius:8px;box-shadow:0 8px 24px rgba(20,24,38,.24);'
              : 'border-radius:6px;',
            textStyle: {
              color: '#fff',
              fontSize: overviewTooltip ? 12 : 11
            },
            axisPointer: {
              type: 'line',
              lineStyle: {
                color: overviewTooltip ? '#A9C5FF' : 'rgba(31, 107, 255, 0.28)',
                width: 1,
                type: 'dashed'
              }
            },
            formatter(params) {
              const item = params && params[0]
              if (!item) return ''
              if (!overviewTooltip) {
                return `${item.axisValue}<br/>${translate('收益')}：${Number(item.value).toFixed(2)} USDT`
              }
              const amount = Number(item.value)
              const rawRate = tooltipRates[item.dataIndex]
              const rate = rawRate === null || rawRate === undefined || rawRate === ''
                ? Number.NaN
                : Number(rawRate)
              const amountText = Number.isFinite(amount)
                ? `${amount > 0 ? '+' : ''}${amount.toLocaleString(numberLocale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}`
                : '--'
              const rateText = Number.isFinite(rate)
                ? `${rate > 0 ? '+' : ''}${(rate * 100).toFixed(2)}%`
                : '--'
              return [
                `<div style="color:#AEB4C2;font-size:12px;line-height:18px;">${escapeHtml(item.axisValue)}</div>`,
                `<div style="color:#FFFFFF;font-size:12px;font-weight:700;line-height:19px;">${escapeHtml(translate('累计收益'))} ${amountText} USDT</div>`,
                `<div style="color:#7CE7B4;font-size:12px;font-weight:700;line-height:19px;">${escapeHtml(translate('累计收益率'))} ${rateText}</div>`
              ].join('')
            }
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            show: this.showAxis,
            axisLine: {
              lineStyle: { color: '#e9edf3' }
            },
            axisTick: { show: false },
            axisLabel: {
              color: '#98a0af',
              fontSize: this.axisFontSize,
              margin: 10,
              interval(index) {
                return visibleLabelIndexes.has(index)
              }
            },
            data: labels
          },
          yAxis: {
            type: 'value',
            scale: true,
            show: this.showAxis,
            splitNumber: this.splitNumber === null ? undefined : this.splitNumber,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: {
              show: this.showAxis,
              lineStyle: {
                color: '#eef1f5',
                width: 1
              }
            }
          },
          series: [
            {
              name: translate('收益'),
              type: 'line',
              data: this.points,
              smooth: this.smooth,
              showSymbol: false,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                color: this.color,
                width: this.strokeWidth,
                cap: 'round'
              },
              itemStyle: {
                color: this.color
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {
                    offset: 0,
                    color: this.hexToRgba(this.color, this.areaOpacity)
                  },
                  {
                    offset: 1,
                    color: this.hexToRgba(this.color, 0)
                  }
                ])
              },
              emphasis: {
                focus: 'series',
                scale: true,
                itemStyle: {
                  color: this.color,
                  borderColor: '#fff',
                  borderWidth: 2
                }
              }
            }
          ]
        },
        true
      )
    },
    hexToRgba(color, alpha) {
      const normalized = color.replace('#', '')
      const full = normalized.length === 3
        ? normalized.split('').map(item => item + item).join('')
        : normalized
      const value = Number.parseInt(full, 16)
      const red = (value >> 16) & 255
      const green = (value >> 8) & 255
      const blue = value & 255
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`
    }
  }
}
</script>

<style scoped>
.echarts-line {
  width: 100%;
  height: 100%;
  min-height: 40px;
}
</style>
